import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

export interface PWRFlagsResponse {
  enableFlareWatch: boolean;
  enableProtocolAndEprPreRecruitment: boolean;
  enableSitePreRecruitment: boolean;
  enableSecondaryScreener: boolean;
}

/** Local Protocol template (GraphQL) — PWR Checkbox fields (EnableFlareWatch, etc.). */
export interface PwrLocalProtocolSummary {
  itemId: string;
  protocolId?: string;
  enableFlareWatch?: Field<boolean> | { value?: boolean | string | number };
  enablePreRecruitment?: Field<boolean> | { value?: boolean | string | number };
  enableSecondaryScreener?: Field<boolean> | { value?: boolean | string | number };
}

/**
 * Sitecore Checkbox: layout/JSS often uses boolean true; GraphQL Edge field.value is often "1"/"0".
 */
export function isPwrCheckboxEnabled(field?: { value?: unknown } | null): boolean {
  const value = field?.value;
  return value === true || value === 1 || value === '1';
}

/** Website-level PWR Checkbox fields (PatientWaitingRoomBase PWR Settings in src/items). */
export function isAnyWebsitePwrFlagEnabled(siteSettings: any): boolean {
  return (
    isPwrCheckboxEnabled(siteSettings?.enableFlareWatch) ||
    isPwrCheckboxEnabled(siteSettings?.enablePreRecruitment) ||
    isPwrCheckboxEnabled(siteSettings?.enableSecondaryScreener)
  );
}

/** CDP GetSites `sitesData[0].enableSitePreRecruitment` — boolean from API, not Sitecore. */
export function getSitePreRecruitmentFromCdp(cdpPayload?: any): boolean | undefined {
  const enableSitePreRecruitment = cdpPayload?.sitesData?.[0]?.enableSitePreRecruitment;
  if (typeof enableSitePreRecruitment === 'boolean') {
    return enableSitePreRecruitment;
  }
  return undefined;
}

function toKey(value: string): string {
  return value.replace(/[{}]/g, '').trim().toLowerCase();
}

/**
 * Mirrors sites.api `OverrideSelectedProtocols` PWR fields — Global AND Local when a matching
 * Local Protocol entry exists; Global only when Local Protocol cache has no entry for protocol.
 */
export function mergeProtocolFlagGlobalAndLocal(
  globalProtocolField?: { value?: unknown } | null,
  localProtocolField?: { value?: unknown } | null,
  localProtocol?: PwrLocalProtocolSummary | null
): boolean {
  if (localProtocol == null) {
    return isPwrCheckboxEnabled(globalProtocolField);
  }
  return isPwrCheckboxEnabled(globalProtocolField) && isPwrCheckboxEnabled(localProtocolField);
}

/**
 * Website + protocol PWR flags from layout siteSettings (Checkbox) and Local Protocol cache.
 * Site pre-recruitment from CDP GetSites boolean only.
 */
export function buildPwrFlagsFromSources(
  siteSettings: any,
  globalProtocolFields: any,
  localProtocol?: PwrLocalProtocolSummary | null,
  siteEnablePreRecruitment?: boolean
): PWRFlagsResponse {
  const websiteFlareWatch = isPwrCheckboxEnabled(siteSettings?.enableFlareWatch);
  const websitePreRecruitment = isPwrCheckboxEnabled(siteSettings?.enablePreRecruitment);
  const websiteSecondaryScreener = isPwrCheckboxEnabled(siteSettings?.enableSecondaryScreener);

  const protocolFlareWatch = mergeProtocolFlagGlobalAndLocal(
    globalProtocolFields?.enableFlareWatch,
    localProtocol?.enableFlareWatch,
    localProtocol
  );
  const protocolPreRecruitment = mergeProtocolFlagGlobalAndLocal(
    globalProtocolFields?.enablePreRecruitment,
    localProtocol?.enablePreRecruitment,
    localProtocol
  );
  const protocolSecondaryScreener = mergeProtocolFlagGlobalAndLocal(
    globalProtocolFields?.enableSecondaryScreener,
    localProtocol?.enableSecondaryScreener,
    localProtocol
  );

  return {
    enableFlareWatch: websiteFlareWatch && protocolFlareWatch,
    enableProtocolAndEprPreRecruitment: websitePreRecruitment && protocolPreRecruitment,
    enableSecondaryScreener: websiteSecondaryScreener && protocolSecondaryScreener,
    enableSitePreRecruitment: websitePreRecruitment && siteEnablePreRecruitment === true,
  };
}

/**
 * Returns final PWR flags for one qualified protocol.
 * Website + Global Protocol from layout siteSettings.protocols; Local Protocol from page-props context.pwrLocalProtocols.
 */
export function getPwrFlags(
  qualifiedProtocol: string,
  siteSettings: any,
  localProtocols: Record<string, PwrLocalProtocolSummary> = {},
  siteEnablePreRecruitment?: boolean
): PWRFlagsResponse | null {
  if (!qualifiedProtocol?.trim() || !siteSettings?.protocols?.length) {
    return null;
  }

  const qualifiedKey = toKey(qualifiedProtocol);
  const siteSettingsProtocol = siteSettings.protocols.find((item: any) => {
    const sitecoreId = item?.id ? toKey(String(item.id)) : '';
    const businessId = item?.fields?.protocolId?.value
      ? toKey(String(item.fields.protocolId.value))
      : '';
    return qualifiedKey === sitecoreId || qualifiedKey === businessId;
  });

  if (!siteSettingsProtocol?.fields?.protocolId?.value) {
    return null;
  }

  const protocolBusinessId = String(siteSettingsProtocol.fields.protocolId.value);
  const localProtocol = localProtocols[toKey(protocolBusinessId)];

  return buildPwrFlagsFromSources(
    siteSettings,
    siteSettingsProtocol.fields,
    localProtocol,
    siteEnablePreRecruitment
  );
}

/** CDP GetSites site row — `enableSitePreRecruitment` boolean. */
export function getSitePrFromCdp(siteInfo?: { enableSitePreRecruitment?: boolean }): boolean {
  return siteInfo?.enableSitePreRecruitment === true;
}
